

    import Vue from "vue";
    import Component from "vue-class-component";
    import { Ref } from "vue-property-decorator";
    import { VForm } from "@/vForm";
    import Store from "@/store/store";
    import * as toastr from "toastr";
    import ApiClient from "@/utilities/ApiClient";
    import { appEventBus } from "@/utilities/EventBus";
    import Utils from "@/utilities/Utils";
    import MediaSupport, { IMediaSupportResult } from "@/utilities/MediaSupport";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import SingleTextField from "@/vue/components/RegistrationFields/SingleTextField.vue";
    import MultiLineTextField from "@/vue/components/RegistrationFields/MultiLineTextField.vue";
    import YesNoField from "@/vue/components/RegistrationFields/YesNoField.vue";
    import DateField from "@/vue/components/RegistrationFields/DateField.vue";
    import PhotoCapture from "@/vue/components/PhotoCapture.vue";
    import FileUpload from "@/vue/components/FileUpload.vue";
    import { RegistrationFieldType, SiteRegistrationType } from "@/model/Enums";
    import { ILookupItem, LookupItem } from "@/model/LookupItem";
    import { Site } from "@/model/Site";
    import { Visit } from "@/model/Visit";
    import { Resource } from "@/model/Resource";
    import { IRegisterResponse } from "@/model/IRegisterResponse";
    import { RegistrationAnswer } from "@/model/RegistrationAnswer";

    enum ViewState {
        None = 0,
        ShowVisitForm,
        ShowSeenBefore,
        ShowSendFailed,
        ShowWaitForLink,
        ShowImmediateLink
    }

    // guid for max int = 2147483647
    const otherCompanyID = "36ceb4f9-271b-4d64-ff02-f14237aad652";

    @Component({
        components: { DatePicker, ApiButton, SingleTextField, MultiLineTextField, YesNoField, DateField, PhotoCapture, FileUpload }
    })
    export default class SiteRegister extends Vue {

        // need this to be able to see the enum in the template!
        // eslint-disable-next-line
        ViewState: any = ViewState;

        async mounted(): Promise<void> {
            MediaSupport.check((result: IMediaSupportResult) => {
                console.log("DEVICE SUPPORT: ", result);
                this.supportsVideoCamera = result.hasWebcam;
                //this.supportsVideoCamera = false;
            });

            appEventBus.on("visit-register", this.onVisitRegister)

            this.siteID = this.$route.params.siteID;
            await Promise.all([
                (async () => {
                    this.visitorCompanyList = await ApiClient.get(`/api/visitorCompany/lookups?siteID=${this.siteID}`);
                })(),
                (async () => {
                    this.sponsorList = await ApiClient.get(`/api/siteRecipient/lookups?siteID=${this.siteID}&justSponsors=1`);
                })(),
                (async () => {
                    this.pathwayList = await ApiClient.get(`/api/pathway/lookups?siteID=${this.siteID}`);
                })(),
                this.loadSite(),
            ]);
            this.reset();
        }

        beforeDestroy(): void {
            appEventBus.off("visit-register", this.onVisitRegister);
        }

        //
        // -- props and data
        //

        siteID: string = "";
        site: Site = new Site();
        visit: Visit = new Visit();

        viewState: ViewState = ViewState.ShowVisitForm;

        visitorCompanyList: Array<ILookupItem> = [];
        get visitorCompanyOptions(): Array<ILookupItem> {
            const options = this.visitorCompanyList.filter(lu => !Utils.isEmptyId(lu.id) && !Utils.hasDateValue(lu.deleted));
            options.push(LookupItem.createLookup(otherCompanyID, "Other..."));
            return options;
        }

        sponsorList: Array<ILookupItem> = [];
        get sponsorOptions(): Array<ILookupItem> { return Utils.optionsFilter(this.sponsorList); }

        pathwayList: Array<ILookupItem> = [];
        get pathwayOptions(): Array<ILookupItem> { return Utils.optionsFilter(this.pathwayList); }

        @Ref("visitForm") private readonly visitForm!: VForm;

        supportsVideoCamera: boolean = true;

        get extraRegistrationAnswersToShow(): Array<RegistrationAnswer> {
            return this.visit.extraRegistrationAnswers.filter(a => {

                // hide if only for a different pathway
                if(!a.canShowForPathway(this.visit.pathwayID)) return false;

                // now check field dependencies
                if(a.fieldType === RegistrationFieldType.YesNo) return true;
                if(!a.showIf.hasValue) return true;
                const dependentOn = this.visit.extraRegistrationAnswers.find(i => i.key === a.showIf.key);
                if(!dependentOn) return true;
                return dependentOn.value === a.showIf.value;
            });
        }

        //
        // -- methods
        //

        onVisitRegister(event: CustomEvent<any>) {
            console.log("#### visit-register"); // ???
        }

        // this is f***ing genuis: https://stackoverflow.com/questions/54963395/best-way-to-dynamically-change-the-order-of-block-level-elements-in-vue
        getDisplayOrder(columnName: string): string {
            if(columnName === "companyOther") columnName = "companyID";
            const index = this.site.standardRegistrationFields.map(f => f.columnName).indexOf(columnName);
            return index === -1 ? "" : `order-${index + 1}`;
        }

        getLabel(columnName: string): string {
            if(columnName === "companyOther") {
                const item = this.site.standardRegistrationFields.find(f => f.columnName === "companyID");
                return (item?.label ?? "Company") + " - Other";
            }
            const item = this.site.standardRegistrationFields.find(f => f.columnName === columnName);
            return item?.label ?? columnName;
        }

        shouldShow(columnName: string): boolean {
            if(columnName === "pathwayID") {
                // if only one option, no need to show!
                return this.pathwayOptions.length > 1;
            }
            if(columnName === "companyOther") {
                const item = this.site.standardRegistrationFields.find(f => f.columnName === "companyID");
                return (item?.isHidden !== true) && (this.visit.companyID === otherCompanyID);
            }
            // else, use configured value
            const item = this.site.standardRegistrationFields.find(f => f.columnName === columnName);
            return !(item?.isHidden ?? false);
        }

        validateStandardField(columnName: string, value: string|number|Date|Resource): boolean|string {
            const item = this.site.standardRegistrationFields.find(f => f.columnName === columnName);
            if(!item?.isMandatory) return true;
            let message = "";
            if(columnName === "email") {
                if(!value) {
                    message = item.label + " is required";
                }
                else {
                    message = Utils.isValidEmail(String(value)) ? "" : "Please enter a valid email";
                }
            }
            else if(columnName === "plannedArrival") {
                if (!value || typeof value !== "object") {
                    message =  "Please choose a date";
                }
                else {
                    const today = new Date(new Date().toDateString()); // date without time
                    message = value >= today ? "" : "Please choose a future date";
                }
            }
            else if(columnName === "photoResourceID") {
                if(value instanceof Resource) {
                    const resource = value as Resource;
                    if (!resource || !resource.originalFilename) {
                        message = "Photo not uploaded";
                    }
                }
                else if(value instanceof File) {
                    const file = value as File;
                    if(!file) {
                        message = "Photo is required";
                    }
                }
                else {
                    message = "Photo is missing";
                }
            }
            else {
                message = value
                    ? ""
                    : item.label + " is required";
            }
            if(message) console.warn(`Validation FAILED for ${item.label}: `, message);
            return message ? message : true;
        }

        onPhotoChanged(e: any): void {
            console.log("PHOTO CHOSEN: ", e);
        }

        reset(): void {
            Utils.resetObject(this.visit);
            // apparently we want it to default to the first pathway
            this.visit.pathwayID = String(this.pathwayOptions[0]?.id);
            this.visit.extraRegistrationAnswers =
                this.site.extraRegistrationFields
                    .map(f => RegistrationAnswer.fromRegistrationField(f, this.pathwayOptions.map(o => String(o.id))));
            this.visitForm?.resetValidation();
        }

        siteImageSource(site: Site): string {
            return site?.imageResource?.sourceUrl ?? require("@/images/missing.png");
        }

        async loadSite(): Promise<void> {
            Utils.resetObject(this.site);
            if(!Utils.isEmptyId(this.siteID)) {
                const data = await ApiClient.get(`/api/site/Load?id=${this.siteID}`);
                this.site.update(data);
                await Store.dispatch("setSite", this.site);
            }
            else {
                this.site.name = "Failed to load site.  ID = " + this.siteID;
            }
        }

        async save(): Promise<void> {
            if (!this.visitForm.validate()) {
                console.warn("Validation failed ", this.visitForm)
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }
            const response: IRegisterResponse = await ApiClient.post("/api/visit/register", this.visit, "visit-register");
            if(response.isSuccess) {
                if(this.visit.isNew) this.visit.id = response.newID;
                this.viewState = this.site.registrationType === SiteRegistrationType.WithoutEmail
                    ?  ViewState.ShowImmediateLink
                    :  ViewState.ShowWaitForLink;
            }
            else {
                this.viewState = ViewState.ShowSendFailed;
                const messagesHtml = "<ul>" + response.messages.map(m => `<li style='margin-left:30px'>${m}</li>`).join("") + "</ul>";
                toastr.error(messagesHtml, "Registration Failed", {
                    escapeHtml: false,
                    closeButton: true,
                    timeOut: 0
                });
            }
        }
    }
